import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { useMemo } from 'react';
import { ArtifactGrid, artifactMatches } from './artifact-grid';
import { useArtifactIndex } from './use-artifact-index';

/**
 * The artifacts of the account, or of a conversation: the types its apps declare and the generic
 * files the conversation changed, with live previews. `onOpen` opens one in its type's app (a tab
 * of the conversation's panel; outside one, the app's page).
 */
export function AppArtifactsSection({
  query = '',
  conversation,
  type,
  onOpen,
  empty = null,
  refreshKey,
}: {
  query?: string;
  conversation?: string;
  /** Only the artifacts of this type title. */
  type?: string;
  onOpen: (artifact: ArtifactListingEntry) => void;
  empty?: React.ReactNode;
  refreshKey?: unknown;
}) {
  const { artifacts, error, loading } = useArtifactIndex(conversation, { refreshKey });
  const shown = useMemo(
    () =>
      artifacts.filter(
        (artifact) =>
          artifactMatches(artifact, query) && (type === undefined || artifact.typeTitle === type),
      ),
    [artifacts, query, type],
  );
  if (error !== null && artifacts.length === 0)
    return (
      <InlineMessage title="Artifacts are unavailable." tone="error">
        {error}
      </InlineMessage>
    );
  if (loading && artifacts.length === 0) return null;
  if (shown.length === 0) return <>{empty}</>;
  return (
    <section aria-label="Artifacts" className="space-y-2">
      <ArtifactGrid artifacts={shown} onOpen={onOpen} />
    </section>
  );
}
